import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { servicesData } from '../mock';
import { Wrench, Leaf, Flower2, Cloud, Star, ChevronDown, Phone, Shield, Award } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';

const iconMap = {
  Wrench: <Wrench size={32} />,
  Leaf: <Leaf size={32} />,
  Flower2: <Flower2 size={32} />,
  Cloud: <Cloud size={32} />
};

export const Handyman = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const data = servicesData.handyman;

  const expertiseAreas = [
    { title: 'Bricolage', tasks: ['Montage de meubles', 'Fixations murales', 'Petites réparations', 'Installation électroménager'] },
    { title: 'Jardinage', tasks: ['Tonte de pelouse', 'Taille de haies', 'Désherbage', 'Plantation'] }
  ];

  const testimonials = [
    {
      name: 'Marc B.',
      rating: 5,
      comment: 'Intervention rapide et efficace pour le montage de mes meubles IKEA. Je recommande !',
      service: 'Montage meubles'
    },
    {
      name: 'Isabelle M.',
      rating: 5,
      comment: 'Mon jardin n\'a jamais été aussi bien entretenu. Travail soigné et conseils précieux.',
      service: 'Entretien jardin'
    }
  ];

  const faqs = [
    {
      question: 'Fournissez-vous les outils ?',
      answer: 'Oui, nos intervenants viennent avec tout l\'outillage professionnel nécessaire à la réalisation des travaux.'
    },
    {
      question: 'Faites-vous des devis gratuits ?',
      answer: 'Absolument ! Nous établissons un devis gratuit et sans engagement après évaluation de vos besoins.'
    },
    {
      question: 'Êtes-vous assurés ?',
      answer: 'Oui, tous nos intervenants sont couverts par une assurance responsabilité civile professionnelle.'
    },
    {
      question: 'Intervenez-vous en urgence ?',
      answer: 'Nous pouvons intervenir rapidement selon la disponibilité. Contactez-nous pour connaître nos délais.'
    }
  ];

  return (
    <div className="page-service">
      <section className="service-hero">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <h1 className="display-medium">{data.title}</h1>
            <p className="body-medium hero-subtitle">{data.description}</p>
          </ScrollReveal>
        </div>
      </section>

      <section className="service-image-section">
        <div className="container">
          <ScrollReveal animation="zoom-in">
            <div className="service-images-dual">
              <img 
                src="/images/handyman.jpg" 
                alt="Bricolage" 
                className="service-dual-image"
              />
              <img 
                src="/images/handyman-2.jpg" 
                alt="Jardinage" 
                className="service-dual-image"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="expertise-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Nos Domaines d'Expertise</h2>
            </div>
          </ScrollReveal>
          <div className="expertise-grid">
            {expertiseAreas.map((area, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="expertise-card">
                  <h3 className="heading-2">{area.title}</h3>
                  <ul className="tasks-list">
                    {area.tasks.map((task, i) => (
                      <li key={i}>
                        <Wrench size={18} />
                        <span>{task}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="service-details">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Nos Services</h2>
            </div>
          </ScrollReveal>
          <div className="network-grid">
            {data.services.map((service, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="network-card service-detail-card">
                  <div className="feature-icon">{iconMap[service.icon]}</div>
                  <h3 className="heading-2">{service.title}</h3>
                  <p className="body-medium">{service.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Safety & Quality */}
      <section className="safety-section">
        <div className="container">
          <div className="safety-content">
            <ScrollReveal animation="fade-right">
              <div className="safety-text">
                <h2 className="heading-1">Sécurité et Qualité</h2>
              <p className="body-medium">
                Tous nos travaux sont réalisés dans le respect des normes de sécurité et de qualité.
              </p>
            </div>
            </ScrollReveal>
            <ScrollReveal animation="fade-left" delay={200}>
              <div className="safety-stats">
                <div className="stat-badge">
                  <Shield size={32} />
                  <div className="stat-info">
                    <strong>100%</strong>
                    <span>Assurés</span>
                  </div>
                </div>
                <div className="stat-badge">
                  <Award size={32} />
                  <div className="stat-info">
                    <strong>10 ans</strong>
                    <span>d'Expérience</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Avis Clients</h2>
            </div>
          </ScrollReveal>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="testimonial-card">
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} fill="var(--brand-primary)" color="var(--brand-primary)" />
                    ))}
                  </div>
                  <p className="testimonial-comment">"{testimonial.comment}"</p>
                  <div className="testimonial-author">
                    <div className="author-info">
                      <strong>{testimonial.name}</strong>
                      <span>{testimonial.service}</span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Questions Fréquentes</h2>
            </div>
          </ScrollReveal>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 50}>
                <div className="faq-item">
                  <button 
                    className={`faq-question ${openFaq === index ? 'active' : ''}`}
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown size={24} className={`faq-icon ${openFaq === index ? 'rotated' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="faq-answer">
                      <p className="body-medium">{faq.answer}</p>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="cta-content">
              <h2 className="heading-1">Besoin d'aide pour vos travaux ?</h2>
              <p className="body-medium">
                Confiez vos petits travaux à nos experts qualifiés
              </p>
              <div className="cta-buttons">
                <Link to="/contact#devis" className="btn-primary btn-cta">Demander un devis</Link>
                <a href="tel:0481119591" className="btn-secondary btn-cta">
                  <Phone size={20} />
                  04 81 11 95 91
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};